import { BreakOption } from '@types';
import { OgpApiService } from './ogp-api-service';
import {
  BulkShiftCreationCSVUploadPayload,
  BulkShiftCreationCSVUploadResponse,
  GetCalendarShiftPlanningListItemsParams,
  GetCalendarShiftPlanningListItemsResponse,
  GetShiftEligiblePartnerResponse,
  GetShiftPartnerResponse,
  GetTableShiftPlanningListItemsParams,
  GetTableShiftPlanningListItemsResponse,
  OpenShiftToPartnersRequest,
} from './shift-service.types';

export class ShiftService {
  constructor(private readonly ogpApiService: OgpApiService) {}

  public getShiftPartners = async (shiftId: string) => {
    const response = await this.ogpApiService.get<GetShiftPartnerResponse>({
      path: `shifts/${shiftId}/partners`,
    });
    return response;
  };

  /**
   * Forward shift to selected partners
   */
  public openShiftToPartners = async ({ shiftId, partnerIds }: OpenShiftToPartnersRequest) => {
    return await this.ogpApiService.update({
      path: `shifts/${shiftId}/partners`,
      body: { partnerIds },
    });
  };

  /**
   * Get list of partners the shift can be forwarded to
   */
  public getShiftEligiblePartners = async (shiftId: string) => {
    const response = await this.ogpApiService.get<GetShiftEligiblePartnerResponse>({
      path: `shifts/${shiftId}/eligible-partners`,
    });
    return response;
  };

  /**
   * Get the planning overview for shifts
   */
  public getCalendarShiftPlanningListItems = async (
    params: GetCalendarShiftPlanningListItemsParams
  ) => {
    const response = await this.ogpApiService.get<GetCalendarShiftPlanningListItemsResponse>({
      path: 'shifts/planning',
      params,
    });
    return response;
  };

  public getTableShiftPlanningListItems = async (params: GetTableShiftPlanningListItemsParams) => {
    const response = await this.ogpApiService.get<GetTableShiftPlanningListItemsResponse>({
      path: 'shifts/planning/list',
      params,
    });
    return response;
  };

  /**
   * Get breakoptions
   */
  public getBreakOptions = async () => {
    const response = await this.ogpApiService.get<{ breakOptions: BreakOption[] }>({
      path: `shifts/get-break-options`,
    });

    return response;
  };

  /**
   * Mark a shift as archived in the database when the user
   * wants to delete a shift
   */
  public archiveShift = async (shiftId: string) => {
    return await this.ogpApiService.update({
      path: `shifts/${shiftId}/archive`,
    });
  };

  /**
   * Upload CSV for bulk shift creation
   */
  public bulkShiftCreationCSVUpload = async (file: BulkShiftCreationCSVUploadPayload) => {
    const formData = new FormData();
    formData.append('file', file.file);
    return await this.ogpApiService.create<BulkShiftCreationCSVUploadResponse>({
      path: 'shifts/process-csv',
      body: formData,
    });
  };
}
